import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik, FormikProps } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import {
  ExpenseRequest,
  PerksReimbursementDetailsResponse,
  PerksReimbursementDueResponse,
  ReimbursePerksExpensesCommand,
} from 'core'
import getPerksReimbursementDue from 'core/application/adminDashboard/reimbursements/perks/getPerksReimbursementDue'
import submitPerksReimbursementsDue from 'core/application/adminDashboard/reimbursements/perks/submitPerksReimbursementsDue'
import OrderedPageFilter from 'core/application/commons/orderedPageFilter'
import { ReimbursementMethod } from 'core/domain/expenses/reimbursementMethod'

import { useLoginUserV2 } from 'common/hooks/useLoginUser'

import { Button, Col, DefaultDatePickerV2, Notification, Row, Table, Tooltip } from 'common/components'
import { downloadCSV } from 'common/downloadFile'
import { formatMoney } from 'common/moneyFormatter'

import { useCommandRequestV2, useQueryRequestV2 } from '../../../../common/hooks'
import SelectReimburesementMethod, {
  reimbursementMethodsTitleMap,
} from '../../groupHra/reimbursementsDue/selectReimbursementMethod'
import DropdownReimbursementDueAmount from './dropdownReimbursementsDueAmount'
import ApprovePerksReimbursementsDueModal from './perksApproveReimbursementDueModal'
import PerksTotalReimbursementsDue from './perksTotalReimbursementsDue'

import './perksTotalReimbursementsDue/ReimbursementsDue.less'
import './styles.less'

type PerksReimbursementDueProps = {
  planPeriodId: string
  onApproved?: () => void
}

const PerksReimbursementDue: React.FC<PerksReimbursementDueProps> = (props) => {
  const { t: translate } = useTranslation()
  const { user } = useLoginUserV2()
  const [loadingReimbursementsDue, setLoadingReimbursementsDue] = useState<boolean>(false)
  const [selectedValues, setSelectedValues] = useState([])
  const [filter, setFilter] = useState<OrderedPageFilter>({
    pageNumber: 1,
    pageSize: 25,
    orderColumn: 'firstName',
    orderDescending: true,
  })
  const [overrideReimbursementsDueDropdownVisible, setOverrideReimbursementsDueDropdownVisible] = useState<
    DropdownVisible[]
  >([])
  const [reimbursementsDueToSubmit, setReimbursementsDueToSubmit] = useState<ReimbursementsDueToSubmit[]>([])
  const [approveModalVisible, setApproveModalVisible] = useState<boolean>(false)
  const [approveLoading, setApproveLoading] = useState<boolean>(false)
  const [reimbursementsDue, setReimbursementsDue] = useState<PerksReimbursementDueResponse[]>([])

  const { sendCommand: sendPerksReimbursementDue } = useCommandRequestV2(
    (valuesToSubmit: ReimbursePerksExpensesCommand) => submitPerksReimbursementsDue(valuesToSubmit),
  )

  const { sendQuery: sendPerksReimbursement } = useQueryRequestV2<PerksReimbursementDueResponse[]>({
    queryFn: () => getPerksReimbursementDue(props.planPeriodId, filter),
  })

  const columns: any[] = [
    {
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-due.first-name'),
      dataIndex: 'firstName',
      name: 'firstName',
      key: 'firstName',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-due.last-name'),
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-due.category'),
      dataIndex: 'expenseCategoryName',
      key: 'expenseCategoryName',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      align: 'right',
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-due.available-allowance'),
      dataIndex: 'availableAllowance',
      key: 'availableAllowance',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (item: number) => {
        return formatMoney(item)
      },
    },

    {
      align: 'right',
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-due.expenses-ytd'),
      dataIndex: 'expensesYTD',
      key: 'expensesYTD',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (item: number) => {
        return formatMoney(item)
      },
    },
    {
      align: 'right',
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-due.reimbursements-ytd'),
      dataIndex: 'reimbursementsApprovedYTD',
      key: 'reimbursementsApprovedYTD',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (item: number) => {
        return formatMoney(item)
      },
    },
    {
      align: 'right',
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-due.reimbursements-due'),
      dataIndex: 'reimbursementsDue',
      key: 'reimbursementsDue',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (reimbursementDue: number, record: PerksReimbursementDueResponse) => {
        return (
          <DropdownReimbursementDueAmount
            item={reimbursementDue}
            record={record}
            translate={translate}
            reimbursementsDueToSubmit={reimbursementsDueToSubmit}
            dropdownVisible={overrideReimbursementsDueDropdownVisible}
            setDropdownVisibility={setDropdownVisibility}
            setReimbursementsDue={selectedReimbursementsDue}
          />
        )
      },
    },
  ]

  const columnToSort: any = columns.find((c) => c.key === filter.orderColumn)
  columnToSort.defaultSortOrder = filter.orderDescending ? 'descend' : 'ascend'

  const setDropdownVisibility = (key: string, visible: boolean): void => {
    const overrideReimbursementsDueDropdownVisibleCopy = [...overrideReimbursementsDueDropdownVisible]
    const dropdownItem = overrideReimbursementsDueDropdownVisibleCopy.find((item) => item.key === key)
    if (dropdownItem) {
      dropdownItem.visible = visible
      setOverrideReimbursementsDueDropdownVisible(overrideReimbursementsDueDropdownVisibleCopy)
    }
  }

  const selectedReimbursementsDue = (key: string, amount: number): void => {
    const reimbursementsDueToSubmitCopy = [...reimbursementsDueToSubmit]
    const reimbursementItem = reimbursementsDueToSubmitCopy.find((item) => item.id === key)
    if (reimbursementItem) {
      if (reimbursementItem.originalReimbursementDue === amount) {
        reimbursementItem.overrideReimbursementDue = undefined
      } else {
        reimbursementItem.overrideReimbursementDue = amount
      }
      setReimbursementsDueToSubmit(reimbursementsDueToSubmitCopy)
    }
  }

  useEffect(() => {
    ;(async () => {
      getPerksReimbursementDueResults()
    })()
  }, [filter, props.planPeriodId])

  const getPerksReimbursementDueResults = async () => {
    setLoadingReimbursementsDue(true)
    const result = await sendPerksReimbursement()
    setReimbursementsDue(result)
    setOverrideReimbursementsDueDropdownVisible(
      result.map((item: PerksReimbursementDueResponse) => {
        return {
          key: item.id,
          visible: false,
        }
      }),
    )

    setReimbursementsDueToSubmit(
      result.map((item: PerksReimbursementDueResponse) => {
        return {
          id: item.id,
          originalReimbursementDue: item.reimbursementsDue,
          offerSameAllowance: item.offerSameAllowance,
        }
      }),
    )
    setLoadingReimbursementsDue(false)
  }

  let reimbursementMethodSelectOption: SelectOptionOrOverride | null = null
  const reimbursementMethod = (formik: FormikProps<any>) => {
    const overrideFieldDefaultValue = null
    const selectFieldDefaultValue = null

    if (
      formik.values.reimbursementMethodOverride !== overrideFieldDefaultValue &&
      reimbursementMethodSelectOption !== SelectOptionOrOverride.override
    ) {
      reimbursementMethodSelectOption = SelectOptionOrOverride.override
      formik.setFieldValue('reimbursementMethodSelect', selectFieldDefaultValue)
      return null
    } else if (
      formik.values.reimbursementMethodSelect !== selectFieldDefaultValue &&
      reimbursementMethodSelectOption !== SelectOptionOrOverride.select
    ) {
      reimbursementMethodSelectOption = SelectOptionOrOverride.select
      formik.setFieldValue('reimbursementMethodOverride', overrideFieldDefaultValue)
    }
    return null
  }

  const getSelectedReimbursementMethod = (formik: FormikProps<any>): ReimbursementMethod => {
    return formik.values.reimbursementMethodSelect
  }

  const onTableFilterChanged = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      ...filter,
      orderColumn: sorter.field,
      orderDescending: sorter.order !== 'ascend',
    })
  }

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedValues(selectedRowKeys)
    },
  }

  const getExpensesToSubmit = (): ExpenseRequest[] => {
    let expensesToSubmit: ExpenseRequest[] = []
    try {
      selectedValues.forEach((id: string) => {
        const [companyUserId, categoryId] = id.split(',')
        const amountToReimburse = reimbursementsDueToSubmit.find((item) => item.id === id)

        if (
          amountToReimburse.overrideReimbursementDue !== undefined &&
          amountToReimburse.overrideReimbursementDue > 0
        ) {
          expensesToSubmit.push({
            companyUserId: companyUserId,
            expenseCategoryId: parseInt(categoryId),
            amount: amountToReimburse.overrideReimbursementDue,
          })
        } else {
          expensesToSubmit.push({
            companyUserId: companyUserId,
            expenseCategoryId: parseInt(categoryId),
            amount: amountToReimburse.originalReimbursementDue,
          })
        }
      })
    } catch (error) {
      console.log(error)
    }
    return expensesToSubmit
  }

  const onSubmit = (values: any) => {
    setApproveModalVisible(true)
  }

  const getOfferSameReimbursementValues = (reimbursementsDue: PerksReimbursementDetailsResponse[]) => {
    const resultArray = []
    const employees = reimbursementsDue.filter(
      (x, index, self) => self.findIndex((y) => y.employeeId.split(',')[0] === x.employeeId.split(',')[0]) === index,
    )
    employees.forEach((employee) => {
      const employeeId = employee.employeeId.split(',')[0]
      const reimbursementForEmployee = reimbursementsDue.filter((x) => x.employeeId.split(',')[0] == employeeId)
      let availableAllowance = reimbursementForEmployee[0].availableAllowance
      reimbursementForEmployee.forEach((item) => {
        if (availableAllowance <= 0) {
          return
        }
        if (availableAllowance - item.amountToReimburse < 0) {
          resultArray.push({
            amountToReimburse: availableAllowance,
            employeeFullName: employee.employeeFullName,
          })
          availableAllowance -= item.amountToReimburse
          return
        }
        availableAllowance -= item.amountToReimburse
        resultArray.push({
          amountToReimburse: item.amountToReimburse,
          employeeFullName: employee.employeeFullName,
        })
      })
    })
    return resultArray
  }

  const prepareCsvContent = (reimbursementsDue: any, formik: any) => {
    let content = 'FullName,ScheduleDate,Amount\n'
    if (reimbursementsDue.some((x) => x.offerSameAllowance)) {
      let result = getOfferSameReimbursementValues(reimbursementsDue)
      result.forEach((item) => {
        content += `${item.employeeFullName},${formik.values.scheduled},${item.amountToReimburse}\n`
      })
    } else {
      reimbursementsDue.forEach((item) => {
        content += `${item.employeeFullName},${formik.values.scheduled},${item.amountToReimburse}\n`
      })
    }
    return content
  }

  const onApproveSubmit = async (downloadCsv: boolean, formik: FormikProps<any>) => {
    if (formik.isValid) {
      setApproveLoading(true)
      let valuesToSubmit: ReimbursePerksExpensesCommand = {
        planPeriodId: props.planPeriodId,
        scheduledDate: formik.values.scheduled,
        reimbursementMethod: getSelectedReimbursementMethod(formik),
        otherReimbursementMethod: formik.values.reimbursementMethodOverride,
        expenses: getExpensesToSubmit(),
      }
      const result = await sendPerksReimbursementDue(valuesToSubmit)

      if (downloadCsv) {
        const reimbursementsDue = getListOfReimbursementsDue()
        const content = prepareCsvContent(reimbursementsDue, formik)
        downloadCSV(content, 'PerksReimbursements.csv')
      }
      if (result.errors.length > 0) {
        Notification.open({
          message: result.errors[0],
          type: 'error',
        })
      } else {
        Notification.open({
          message: translate(
            'admin-dashboard.reimbursements.perks-reimbursements-due.reimbursements-due-success-message',
          ),
          type: 'success',
        })
        await getPerksReimbursementDueResults()
        formik.values.reimbursementMethod = null
        formik.values.reimbursementMethodSelect = null
        formik.values.scheduled = null
        if (selectedValues.length == 0) {
          setSelectedValues([])
        }
      }
      if (props.onApproved) {
        props.onApproved()
      }
      setApproveLoading(false)
      setApproveModalVisible(false)
    } else {
      setApproveLoading(false)
      setApproveModalVisible(false)
    }
  }

  const getListOfReimbursementsDue = () => {
    return reimbursementsDueToSubmit
      .filter((item) => selectedValues.some((s: any) => s === item.id))
      .map((item) => {
        var employee = reimbursementsDue.find((r) => r.id === item.id)
        return {
          amountToReimburse:
            item.overrideReimbursementDue !== undefined ? item.overrideReimbursementDue : item.originalReimbursementDue,
          employeeFullName: employee ? `${employee.firstName} ${employee.lastName}` : '',
          employeeId: employee.id,
          offerSameAllowance: employee.offerSameAllowance,
          availableAllowance: employee.availableAllowance,
        }
      })
  }

  const getFinalListOfReimbursementsDue = () => {
    const reimbursementsDue = getListOfReimbursementsDue()
    if (reimbursementsDue.some((x) => x.offerSameAllowance)) {
      return getOfferSameReimbursementValues(reimbursementsDue)
    } else return reimbursementsDue
  }

  const getSubmitButton = (formik: FormikProps<any>): ReactNode => {
    const valid = formik.isValid && selectedValues.length > 0
    const button = (
      <Button type="primary" className="approve_button" icon="far fa-check-circle" htmlType="submit" disabled={!valid}>
        {translate('general.approve')}
      </Button>
    )
    if (!valid) {
      return (
        <Tooltip title={translate('admin-dashboard.reimbursements.reimbursements-due.approve-tooltip')} position="left">
          {button}
        </Tooltip>
      )
    } else {
      return button
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {(formik) => {
        return (
          <form onSubmit={formik.handleSubmit} autoComplete="off" className="perks_reimbursement_due_wrap">
            <Row>
              <Col span={24}>
                <div>
                  <h6 className="color-gray-8">{selectedValues.length} row(s) selected</h6>
                </div>
                <ApprovePerksReimbursementsDueModal
                  translate={translate}
                  visible={approveModalVisible}
                  approveLoading={approveLoading}
                  onCancel={() => {
                    setApproveLoading(false)
                    setApproveModalVisible(false)
                  }}
                  onApprove={(downloadCsv) => onApproveSubmit(downloadCsv, formik)}
                  companyName={user.companyName}
                  scheduled={formik.values.scheduled}
                  reimbursementMethod={getSelectedReimbursementMethodAsString(formik)}
                  reimbursementsDue={getFinalListOfReimbursementsDue()}
                />
                <Table
                  className="table_footer_padding"
                  dataSource={reimbursementsDue}
                  loading={loadingReimbursementsDue}
                  onChange={onTableFilterChanged}
                  columns={columns}
                  rowKey="id"
                  scroll={{ x: 1300 }}
                  rowSelection={rowSelection}
                  pagination={false}
                  footer={(data: PerksReimbursementDueResponse[]) => {
                    return (
                      <PerksTotalReimbursementsDue
                        reimbursementsDue={data}
                        translate={translate}
                        total={getTotalSelectedReimbursementsDue(reimbursementsDueToSubmit, selectedValues)}
                      />
                    )
                  }}
                />
              </Col>
            </Row>

            <Row className="payment_details" gutter={16} justify="end">
              <Col xs={24} sm={24} md={5} lg={5} xl={5} className=" pt-4">
                <SelectReimburesementMethod translate={translate} />
                {reimbursementMethod(formik)}
              </Col>
              <Col xs={24} sm={24} md={6} lg={5} xl={5} className=" pt-4">
                <DefaultDatePickerV2
                  label={translate('admin-dashboard.reimbursements.perks-reimbursements-due.scheduled-label')}
                  name="scheduled"
                  id="scheduled"
                  className="scheduled_datepicker"
                  format={'MM/DD/YYYY'}
                  disabledDate={(d) => !d || d.isBefore(moment().add(-1, 'days'))}
                />
              </Col>
              <Col xs={24} sm={24} md={5} lg={5} xl={4} className="approve_button pt-24">
                {getSubmitButton(formik)}
              </Col>
            </Row>
          </form>
        )
      }}
    </Formik>
  )
}

enum SelectOptionOrOverride {
  select = 0,
  override = 1,
}

export interface ReimbursementsDueToSubmit {
  id: string
  originalReimbursementDue: number
  offerSameAllowance: boolean
  overrideReimbursementDue?: number | undefined
}

export interface DropdownVisible {
  key: string
  visible: boolean
}

const initialValues: any = {}

const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    scheduled: Yup.date().typeError(translate('general.required')).required(),
    reimbursementMethodSelect: Yup.string().nullable(),
    reimbursementMethodOverride: Yup.string()
      .nullable()
      .when('reimbursementMethodSelect', {
        is: (reimbursementMethodSelect: string) => reimbursementMethodSelect === null,
        then: Yup.string().trim().required(translate('general.required')).typeError(translate('general.required')),
      }),
  })

const getTotalSelectedReimbursementsDue = (
  reimbursementsDueToSubmit: ReimbursementsDueToSubmit[],
  selectedReimbursementsDueToSubmit: string[] | number[],
): string => {
  const total = reimbursementsDueToSubmit
    .filter((item) => selectedReimbursementsDueToSubmit.some((s: string | number) => s === item.id))
    .reduce(
      (total, next) =>
        total + (next.overrideReimbursementDue ? next.overrideReimbursementDue : next.originalReimbursementDue),
      0,
    )
  return formatMoney(total)
}

const getSelectedReimbursementMethodAsString = (formik: FormikProps<any>): string => {
  if (formik.values.reimbursementMethodSelect === null) {
    return formik.values.reimbursementMethodOverride
  } else {
    return reimbursementMethodsTitleMap.get(formik.values.reimbursementMethodSelect) ?? ''
  }
}

export default PerksReimbursementDue
